// Top-bar language switcher (PLAYER end).
//
// SIBLING: shared-ui/src/admin/components/AdminLanguageSwitcher.tsx
// — visually + behaviourally identical, but reads from useAdminAuth()
// + PATCHes /admin/me-agent/language for the agent app. ANY styling
// or behaviour change you make to this file MUST be mirrored there.
// The two files are kept in lock-step by design ("do not alter the
// premium styling" was an explicit product directive when the agent
// variant was forked).
//
// Two pieces:
//  - A small globe-icon button that lives in the NavBar (always visible,
//    even when logged-out, so a non-English speaker can switch BEFORE
//    they sign in). The button is recognisable by glyph alone — no text
//    needed — so a user who can't read English can still find the
//    "change language" affordance.
//  - A dropdown panel anchored to the navbar (top-right of viewport,
//    just below the navbar's bottom edge). The panel has a hard
//    max-height of (100dvh - navbar - safe-area-bottom) and scrolls
//    INTERNALLY when the 12 languages don't fit — so it can never
//    end up half-hidden above the fold. Selecting a card updates the
//    i18n context, persists to localStorage, and (if the user is
//    signed in) PATCHes /me so the choice follows them across devices.
//
// The whole UI re-renders the moment the language changes — every
// translatable string goes through useT() which is reactive to the
// current language.

import { useEffect, useState } from 'react';
import { useI18n } from '../lib/i18n';
import { SUPPORTED_LANGS } from '../lib/translations';
import type { Lang } from '../lib/translations';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { useToast } from '../lib/toast';
import Icon from './Icon';

export default function LanguageSwitcher() {
  const { lang, setLang, t, chosen } = useI18n();
  const { user, refreshMe, loading } = useAuth();
  const { push } = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<Lang | null>(null);

  // Close on Escape so the modal honours OS-standard dismissal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // First-run discovery (2026-05-25): instead of a blocking modal, the
  // existing dropdown auto-opens ONCE, 3s after the page settles, for a
  // brand-new ANONYMOUS visitor who hasn't chosen a language yet. This
  // shows them the option without walling them off. Gates:
  //   - auth must be resolved (no flash before we know who they are)
  //   - only anonymous (a logged-in user already carries a language)
  //   - only if no language was ever chosen on this device (`chosen`)
  // Picking a language flips `chosen` → true, which both closes the
  // panel (via pick()) and prevents any future auto-open. If they close
  // it without choosing, we don't nag again this load.
  useEffect(() => {
    if (loading || user || chosen) return;
    const id = setTimeout(() => setOpen(true), 3000);
    return () => clearTimeout(id);
  }, [loading, user, chosen]);

  // TEMPORARY pick (2026-08-08). The top-bar picker changes the site
  // language for THIS session only — it does NOT write to the DB. Picking a
  // language applies it instantly and closes the panel. To make a language
  // stick across logins, the user re-opens the panel and taps the small
  // "Save permanently" button that appears only when the current language
  // differs from the saved one (see below). This makes saving deliberate:
  // if you pick another language instead of Save, the panel just closes and
  // nothing is saved — you have to re-open and press Save without changing
  // the selection. On the next login, LanguageSync re-applies the DB-saved
  // language, so a temporary change never silently becomes permanent.
  function pick(code: Lang) {
    if (code !== lang) setLang(code);
    setOpen(false);
  }

  // The saved (DB) language for the logged-in user. When the active language
  // differs from it, we offer the deliberate "Save permanently" action.
  const savedLang = (user?.language as Lang | undefined) ?? null;
  const canSavePermanent = !!user && lang !== savedLang;

  async function savePermanent() {
    if (!user) return;
    setBusy(lang);
    try {
      await api.patch('/me', { language: lang });
      await refreshMe();
      push('success', t('lang.savedPermanent'));
    } catch (e) {
      push('error', (e as Error).message || 'Could not save language');
    } finally {
      setBusy(null);
      setOpen(false);
    }
  }

  const current = SUPPORTED_LANGS.find((l) => l.code === lang) ?? SUPPORTED_LANGS[0];

  return (
    <>
      {/* Trigger — small circular pill with a globe icon and the
          current language code beside it. Globe means "languages" even
          to a user who can't read; the 2-letter code tells them what's
          set. Sits next to ThemeToggle in the NavBar. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('lang.switcher.aria')}
        title={t('lang.switcher.aria')}
        className="inline-flex items-center gap-1 h-10 pl-2 pr-2.5 rounded-full
                   border border-arena-200 dark:border-neon-400/30
                   bg-white/70 dark:bg-arena-800/70
                   text-arena-700 dark:text-neon-300
                   font-bold text-[11px] uppercase tracking-wider
                   active:scale-95 transition"
      >
        <Icon name="language" size={20} filled />
        <span className="tabular-nums">{current.code.toUpperCase()}</span>
      </button>

      {/* Dropdown — anchored to the top-right of the viewport, just
          below the NavBar's bottom edge. Hard top/bottom positioning
          using env(safe-area-inset-*) means it can't drift above the
          fold on phones with notches, and overflow-y:auto on the inner
          panel lets the 12 cards scroll internally if the device is
          short — never half-hidden, always tappable.

          Layered as:
            [60] click-catcher backdrop (closes on outside tap)
            [61] panel itself */}
      {open && (
        <>
          {/* Invisible full-viewport backdrop — captures clicks outside
              the panel to close it. Slight tint so the page recedes. */}
          <div
            className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-[1px]"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          {/* Panel — anchored top-right, scrolls internally */}
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t('lang.switcher.title')}
            onClick={(e) => e.stopPropagation()}
            className="fixed z-[61] right-2 sm:right-4
                       w-[min(20rem,calc(100vw-1rem))]
                       rounded-2xl
                       bg-white dark:bg-arena-800
                       border border-arena-200 dark:border-neon-400/25
                       shadow-2xl overflow-hidden
                       animate-slide-down"
            style={{
              // Sit just below the NavBar (h-12 = 3rem) + the iOS safe
              // area at the top. 0.5rem of breathing room.
              top: 'calc(3rem + env(safe-area-inset-top) + 0.5rem)',
              // Cap height to exactly what fits between the navbar and
              // the bottom safe-area; the inner list scrolls if needed.
              maxHeight:
                'calc(100dvh - 3rem - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 1rem)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header — sticky so it's always visible while scrolling */}
            <div className="px-4 pt-3 pb-2 flex items-start gap-3 border-b border-arena-200 dark:border-arena-600/40 shrink-0">
              <div className="w-9 h-9 rounded-xl grid place-items-center
                              bg-gradient-to-br from-neon-300/40 to-brand-300/30
                              dark:from-neon-400/20 dark:to-brand-500/20
                              text-arena-700 dark:text-neon-300 shrink-0">
                <Icon name="language" size={20} filled />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-display font-extrabold text-sm text-arena-800 dark:text-neon-100 leading-tight">
                  {t('lang.switcher.title')}
                </h2>
                <p className="text-[11px] text-arena-500 dark:text-neon-200/70 leading-snug">
                  {t('lang.switcher.sub')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t('common.cancel')}
                className="shrink-0 w-8 h-8 -mt-0.5 -mr-1 rounded-full grid place-items-center
                           text-arena-500 dark:text-neon-200/70
                           hover:bg-arena-100/70 dark:hover:bg-arena-700/70 transition"
              >
                <Icon name="close" size={20} />
              </button>
            </div>

            {/* "Save permanently" — small, deliberate, and only shown when the
                current (temporary) language differs from the saved one. Picking
                any language above closes the panel WITHOUT saving; to keep a
                language the user re-opens and taps this. */}
            {canSavePermanent && (
              <div className="px-3 pt-2.5 shrink-0">
                <button
                  type="button"
                  onClick={savePermanent}
                  disabled={busy !== null}
                  className="w-full inline-flex items-center justify-center gap-1.5
                             h-9 rounded-lg text-xs font-bold
                             bg-brand-500/15 text-brand-600 dark:text-brand-300
                             hover:bg-brand-500/25 active:scale-[.98] transition
                             disabled:opacity-50"
                >
                  <Icon name="push_pin" size={14} filled />
                  {t('lang.savePermanent')}
                </button>
              </div>
            )}

            {/* Scrollable list — 2-column grid of native-script cards.
                Active language gets a cyan ring + check tick. */}
            <div className="overflow-y-auto overscroll-contain p-3">
              <div className="grid grid-cols-2 gap-2">
                {SUPPORTED_LANGS.map((l) => {
                  const active = l.code === lang;
                  const isBusy = busy === l.code;
                  return (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => !isBusy && pick(l.code)}
                      disabled={busy !== null}
                      aria-pressed={active}
                      dir={l.code === 'ur' ? 'rtl' : 'ltr'}
                      className={`relative text-left p-2.5 rounded-xl border transition active:scale-[.98]
                                  ${active
                                    ? 'border-neon-400 bg-neon-100/70 dark:bg-neon-400/10 shadow-neon-cyan'
                                    : 'border-arena-200 dark:border-arena-600/40 bg-arena-50/60 dark:bg-arena-700/40 hover:border-neon-400/50'}
                                  ${isBusy ? 'opacity-70' : ''}`}
                    >
                      <div className="font-display font-extrabold text-[15px] leading-tight text-arena-800 dark:text-neon-100">
                        {l.native}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-arena-500 dark:text-neon-200/60 mt-0.5">
                        {l.english}
                      </div>
                      {active && (
                        <Icon name="check_circle" filled
                              className="absolute top-1.5 right-1.5 text-neon-600 dark:text-neon-300" size={16} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-arena-200 dark:border-arena-600/40 shrink-0">
              <div className="text-[10px] uppercase tracking-[.22em] text-center text-arena-400 dark:text-neon-200/60">
                {t('lang.switcher.current')}: <b className="font-bold text-arena-700 dark:text-neon-200">{current.native}</b>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
